import React, { useState } from "react";
import { Wallet, Info } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { EmptyState, LoadingState, ErrorState } from "../../components/shared/EmptyState";
import { StatCard } from "../../components/shared/StatCard";
import { navForRole } from "../../components/shared/navConfig";
import { useAuth } from "../../context/AppContext";
import { useApi, inr, fmtDate } from "../../lib/useApi";

const TONE = { Payable: "#10B981", "Work in progress": "#3B82F6", Closed: "#94A3B8" };
const FILTERS = ["All", "Payable", "Work in progress", "Closed"];

export default function PaymentsPage() {
  const { role } = useAuth();
  const { data, loading, error, reload } = useApi("/payments/records");
  const [f, setF] = useState("All");
  const items = (data?.items || []).filter(i => f === "All" || i.payout_status === f);
  const title = role === "student" ? "Earnings" : "Payments";

  return (
    <DashboardShell nav={navForRole(role)} title={title} subtitle="Amounts recorded for allocated tasks and direct work">
      <div data-testid="payments-notice" className="card-surface p-3.5 mb-5 flex gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
        <Info size={15} className="shrink-0 text-blue-500 mt-0.5"/>
        This is a record list only. No online payment gateway is connected yet, so payouts are settled outside the app.
      </div>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <StatCard testId="payments-payable" label="Payable" value={data ? inr(data.totals?.Payable) : "—"} icon={Wallet} accent/>
        <StatCard testId="payments-in-progress" label="Work in progress" value={data ? inr(data.totals?.["Work in progress"]) : "—"} icon={Wallet}/>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map(x => (
          <button key={x} data-testid={`payments-filter-${x.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setF(x)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${f === x ? "border-blue-500/40 bg-blue-500/10 text-blue-500" : ""}`}
                  style={f === x ? {} : { borderColor: "var(--border-strong)", color: "var(--text-muted)" }}>{x}</button>
        ))}
      </div>
      <div className="card-surface p-4 sm:p-5">
        {loading ? <LoadingState/> : error ? <ErrorState message={error} onRetry={reload}/> : !items.length ? (
          <EmptyState icon={Wallet} title="No payment records yet" desc="Records appear automatically when a task is allocated or direct work is assigned." testId="payments-empty"/>
        ) : (
          <table className="table-stack w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-left" style={{ color: "var(--text-dim)" }}>
                <th className="pb-3 font-normal">Work</th>
                {role !== "student" && <th className="pb-3 font-normal">Student</th>}
                {role !== "company" && <th className="pb-3 font-normal">Client</th>}
                <th className="pb-3 font-normal">Date</th><th className="pb-3 font-normal">Amount</th><th className="pb-3 font-normal text-right">Payout</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={`${i.kind}-${i.ref_id}`} data-testid={`payment-row-${i.ref_id}`} className="border-t" style={{ borderColor: "var(--border)" }}>
                  <td className="py-3">
                    <div style={{ color: "var(--text)" }}>{i.title || i.task_id}</div>
                    <div className="text-[11px]" style={{ color: "var(--text-dim)" }}>{i.kind === "direct_work" ? "Direct work" : "Task"} · {i.work_status}</div>
                  </td>
                  {role !== "student" && <td data-label="Student" className="text-xs" style={{ color: "var(--text-muted)" }}><span>{i.student_name || "—"}</span></td>}
                  {role !== "company" && <td data-label="Client" className="text-xs" style={{ color: "var(--text-muted)" }}><span>{i.company_name || "—"}</span></td>}
                  <td data-label="Date" className="text-xs" style={{ color: "var(--text-muted)" }}><span>{fmtDate(i.date)}</span></td>
                  <td data-label="Amount" className="font-mono text-xs" style={{ color: "var(--text)" }}><span>{inr(i.amount)}</span></td>
                  <td data-label="Payout" className="text-right text-xs"><span style={{ color: TONE[i.payout_status] }}>{i.payout_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardShell>
  );
}
